// src/components/Simpanan.jsx
import React, { useEffect, useState, useRef } from "react";
import { API_BASE } from "../api/auth";

const Table = ({ columns, data }) => (
  <div className="overflow-x-auto rounded-2xl border border-slate-100 bg-white">
    <table className="w-full text-sm">
      <thead className="bg-slate-50">
        <tr>
          {columns.map((c, i) => (
            <th key={i} className="text-left font-semibold px-4 py-3 text-slate-600">
              {c}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {data.length === 0 && (
          <tr>
            <td colSpan={columns.length} className="px-4 py-3 text-slate-400">
              Belum ada data simpanan.
            </td>
          </tr>
        )}
        {data.map((row, i) => (
          <tr key={i} className="border-t">
            {row.map((cell, j) => (
              <td key={j} className="px-4 py-3 text-slate-700">{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const SectionHeader = ({ title, children }) => (
  <div className="flex items-center justify-between mb-4">
    <h2 className="text-lg font-semibold">{title}</h2>
    <div className="flex items-center gap-2">{children}</div>
  </div>
);

const MemberSearchInput = ({ api, onChange }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const isSelectedRef = useRef(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (isSelectedRef.current) {
      isSelectedRef.current = false;
      return;
    }
    if (query.length < 2) { setResults([]); setOpen(false); return; }
    const timer = setTimeout(async () => {
      try {
        setLoading(true);
        const { data } = await api.get(`/members?search=${encodeURIComponent(query)}&per_page=10`);
        const list = data.data || data;
        setResults(list);
        setOpen(true);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [query, api]);

  const handleSelect = (member) => {
    isSelectedRef.current = true;
    setSelected(member);
    setQuery(member.name);
    setResults([]);
    setOpen(false);
    onChange(member.id);
  };

  const handleClear = () => {
    isSelectedRef.current = false;
    setSelected(null);
    setQuery("");
    setResults([]);
    setOpen(false);
    onChange("");
  };

  return (
    <div className="relative" ref={ref}>
      <div className="relative">
        <input
          className="rounded-xl border px-3 py-2 w-full pr-8"
          placeholder="Ketik nama atau no. anggota..."
          value={query}
          onChange={(e) => {
            isSelectedRef.current = false;
            setSelected(null);
            setQuery(e.target.value);
            onChange("");
          }}
          autoComplete="off"
        />
        {(query || selected) && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600 text-sm"
          >
            ×
          </button>
        )}
      </div>
      {loading && (
        <div className="absolute z-10 mt-1 w-full rounded-xl border bg-white shadow-lg px-4 py-2 text-xs text-slate-400">
          Mencari...
        </div>
      )}
      {open && results.length > 0 && (
        <div className="absolute z-10 mt-1 w-full rounded-xl border bg-white shadow-lg py-1 max-h-48 overflow-y-auto">
          {results.map((m) => (
            <button
              key={m.id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleSelect(m)}
              className="w-full text-left px-4 py-2 text-xs hover:bg-slate-50 text-slate-700"
            >
              <span className="font-medium">{m.name}</span>
              <span className="ml-2 text-slate-400">{m.member_no}</span>
            </button>
          ))}
        </div>
      )}
      {open && results.length === 0 && !loading && (
        <div className="absolute z-10 mt-1 w-full rounded-xl border bg-white shadow-lg px-4 py-2 text-xs text-slate-400">
          Anggota tidak ditemukan
        </div>
      )}
      {selected && (
        <p className="mt-1 text-xs text-slate-400">
          ID: {selected.id} · {selected.member_no}
        </p>
      )}
    </div>
  );
};

export default function SimpananPage({ api, auth }) {
  const [simpanan, setSimpanan] = useState([]);
  const [loading, setLoading] = useState(false);
  const [nominalWajib, setNominalWajib] = useState("150000");
  const [form, setForm] = useState({
    member_id: "",
    type: "",
    amount: "",
    description: "",
    reference: "",
  });

  const userRole = auth?.user?.role || "anggota";
  const isAdmin = userRole === "administrator" || userRole === "pegawai";
  const userId = auth?.user?.id;

  useEffect(() => {
    fetchSimpanan();
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSettings = async () => {
    try {
      const { data } = await api.get("/settings");
      if (data?.simpanan_wajib_nominal?.value) {
        setNominalWajib(data.simpanan_wajib_nominal.value);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchSimpanan = async () => {
    try {
      setLoading(true);
      const endpoint = isAdmin ? "/savings" : `/savings?member_id=${userId}`;
      const { data } = await api.get(endpoint);
      setSimpanan(data.data || data);
    } catch (e) {
      console.error(e);
      alert("Gagal memuat data simpanan. Pastikan backend berjalan & token valid.");
    } finally {
      setLoading(false);
    }
  };

  const handleTypeChange = (e) => {
    const selectedType = e.target.value;
    setForm((f) => ({
      ...f,
      type: selectedType,
      amount: selectedType === "wajib" ? nominalWajib : f.amount,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.member_id) {
      alert("Silakan pilih anggota terlebih dahulu.");
      return;
    }
    try {
      const payload = {
        member_id: parseInt(form.member_id),
        type: form.type,
        amount: parseFloat(form.amount),
        description: form.description,
        reference: form.reference,
      };
      await api.post("/savings", payload);
      await fetchSimpanan();
      setForm({ member_id: "", type: "", amount: "", description: "", reference: "" });
      alert("Simpanan berhasil ditambahkan");
    } catch (e) {
      console.error(e);
      alert("Gagal menyimpan: " + (e.response?.data?.message || e.message));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Hapus simpanan ini?")) return;
    try {
      await api.delete(`/savings/${id}`);
      await fetchSimpanan();
      alert("Simpanan berhasil dihapus");
    } catch (e) {
      console.error(e);
      alert("Gagal menghapus: " + (e.response?.data?.message || e.message));
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("id-ID", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div className="space-y-6">
      <SectionHeader title="Data Simpanan">
        <span className="text-sm text-slate-500">
          API: {API_BASE}/api/savings
        </span>
      </SectionHeader>

      {/* Form Tambah Simpanan - Hanya untuk Admin/Pegawai */}
      {isAdmin && (
        <form
          onSubmit={handleSubmit}
          className="rounded-2xl bg-white border p-5 grid md:grid-cols-2 lg:grid-cols-5 gap-3 items-start"
        >
          {/* Member Search - span 2 kolom biar tidak sempit */}
          <div className="md:col-span-2 lg:col-span-2">
            <MemberSearchInput
              api={api}
              onChange={(id) => setForm((f) => ({ ...f, member_id: id }))}
            />
          </div>
          <select
            className="rounded-xl border px-3 py-2"
            value={form.type}
            onChange={handleTypeChange}
            required
          >
            <option value="" disabled>Jenis Simpanan</option>
            <option value="pokok">Simpanan Pokok</option>
            <option value="wajib">Simpanan Wajib</option>
            <option value="sukarela">Simpanan Sukarela</option>
          </select>
          <input
            className="rounded-xl border px-3 py-2"
            placeholder="Jumlah"
            type="number"
            step="0.01"
            min="0"
            value={form.amount}
            onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
            required
          />
          <input
            className="rounded-xl border px-3 py-2"
            placeholder="Referensi"
            value={form.reference}
            onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))}
            required
          />
          <input
            className="rounded-xl border px-3 py-2 lg:col-span-4"
            placeholder="Keterangan (opsional)"
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
          />
          <button className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl py-2 md:col-span-2 lg:col-span-5">
            Tambah Simpanan
          </button>
        </form>
      )}

      {/* Tabel Simpanan */}
      <div className="rounded-2xl border bg-white">
        {loading ? (
          <div className="p-5 text-sm text-slate-500">Memuat data...</div>
        ) : (
          <Table
            columns={["ID", "Nama Anggota", "Jenis Simpanan", "Jumlah", "Referensi", "Tanggal", "Keterangan", "Aksi"]}
            data={(simpanan || []).map((s) => [
              s.id,
              s.member?.name || "-",
              s.type || s.saving_type || "-",
              formatCurrency(parseFloat(s.amount || 0)),
              s.reference || "-",
              formatDate(s.created_at || s.date),
              s.description || "-",
              isAdmin ? (
                <div className="flex gap-2" key={`act-${s.id}`}>
                  <button
                    onClick={() => handleDelete(s.id)}
                    className="px-3 py-1 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 text-xs"
                  >
                    Hapus
                  </button>
                </div>
              ) : (
                <span key={`act-${s.id}`} className="text-slate-400 text-xs">-</span>
              ),
            ])}
          />
        )}
      </div>

      {/* Summary Card */}
      {simpanan.length > 0 && (
        <div className="grid md:grid-cols-3 gap-4">
          <div className="rounded-2xl bg-gradient-to-br from-blue-500 to-blue-600 text-white p-5">
            <div className="text-sm opacity-90 mb-1">Total Simpanan Pokok</div>
            <div className="text-2xl font-bold">
              {formatCurrency(
                simpanan
                  .filter((s) => ["pokok", "Pokok"].includes(s.type || s.saving_type))
                  .reduce((sum, s) => sum + parseFloat(s.amount || 0), 0)
              )}
            </div>
          </div>
          <div className="rounded-2xl bg-gradient-to-br from-green-500 to-green-600 text-white p-5">
            <div className="text-sm opacity-90 mb-1">Total Simpanan Wajib</div>
            <div className="text-2xl font-bold">
              {formatCurrency(
                simpanan
                  .filter((s) => ["wajib", "Wajib"].includes(s.type || s.saving_type))
                  .reduce((sum, s) => sum + parseFloat(s.amount || 0), 0)
              )}
            </div>
          </div>
          <div className="rounded-2xl bg-gradient-to-br from-purple-500 to-purple-600 text-white p-5">
            <div className="text-sm opacity-90 mb-1">Total Simpanan Sukarela</div>
            <div className="text-2xl font-bold">
              {formatCurrency(
                simpanan
                  .filter((s) => ["sukarela", "Sukarela"].includes(s.type || s.saving_type))
                  .reduce((sum, s) => sum + parseFloat(s.amount || 0), 0)
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}